import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Challenge,
  GradeResult,
  PitchData,
  Pitcher,
  Submission,
  getChallenge,
  getPitchData,
  listMySubmissions,
  submitSolution,
} from "../repository/challengeRepository";
import ExamplePitchers from "./ExamplePitchers";
import { runOverPitchers, RunResult, SampleResult } from "./runner";

// Well-known pitchers with contrasting 2025 profiles (changeup vs. none,
// starter workloads vs. closer workloads, deep vs. narrow arsenals), so the
// examples produce a mix of outputs on most challenges.
const EXAMPLE_NAMES = [
  "Skubal, Tarik",
  "Yamamoto, Yoshinobu",
  "Duran, Jhoan",
  "Clase, Emmanuel",
];

const pickExamplePitchers = (pitchers: Pitcher[]): Pitcher[] => {
  const byName = new Map(pitchers.map((p) => [p.name, p]));
  const picked = EXAMPLE_NAMES.map((n) => byName.get(n)).filter(
    (p): p is Pitcher => p !== undefined
  );
  // If the data set changes and some names disappear, fill from the top.
  for (const p of pitchers) {
    if (picked.length >= EXAMPLE_NAMES.length) break;
    if (!picked.includes(p)) picked.push(p);
  }
  return picked;
};

const ChallengePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [pitchData, setPitchData] = useState<PitchData | null>(null);
  const [code, setCode] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<RunResult | null>(null);
  const [grade, setGrade] = useState<GradeResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);

  const refreshSubmissions = useCallback((challengeId: string) => {
    listMySubmissions(challengeId).then(setSubmissions).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([getChallenge(id), getPitchData()])
      .then(([ch, pd]) => {
        setChallenge(ch);
        setPitchData(pd);
        setCode(ch.starter_code || "");
      })
      .catch((e) => setLoadError(e.message))
      .finally(() => setLoading(false));
    refreshSubmissions(id);
  }, [id, refreshSubmissions]);

  const examplePitchers = useMemo(
    () => (pitchData ? pickExamplePitchers(pitchData.pitchers) : []),
    [pitchData]
  );

  const sampleResults = useMemo<Map<string, SampleResult> | null>(() => {
    if (!runResult || runResult.error) return null;
    return new Map(runResult.samples.map((s) => [s.pitcherId, s]));
  }, [runResult]);

  const handleRun = async (): Promise<RunResult | null> => {
    if (!challenge || !pitchData) return null;
    setActionError(null);
    setGrade(null);
    setRunning(true);
    const result = await runOverPitchers(
      code,
      challenge.function_name,
      pitchData.pitchers,
      examplePitchers.map((p) => p.pitcher_id)
    );
    setRunResult(result);
    setRunning(false);
    return result;
  };

  const handleSubmit = async () => {
    if (!challenge) return;
    const result = await handleRun();
    if (!result || result.error) return; // don't submit code that didn't run cleanly
    try {
      const g = await submitSolution(challenge.challenge_id, code, result.count);
      setGrade(g);
      refreshSubmissions(challenge.challenge_id);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Submission failed");
    }
  };

  if (loading) return <div className="panel">Loading challenge…</div>;
  if (loadError) return <div className="panel error">{loadError}</div>;
  if (!challenge || !pitchData) return <div className="panel error">Not found.</div>;

  return (
    <div className="challenge-page">
      <Link to="/" className="back">← All challenges</Link>

      <div className="challenge-grid">
        <section className="prompt-col">
          <h2>
            <span className="ch-order">#{challenge.order}</span> {challenge.title}
          </h2>
          <div className="prompt">
            {challenge.prompt.split("\n\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <h3>Pitch-type legend</h3>
          <div className="legend">
            {Object.entries(challenge.legend).map(([letter, name]) => (
              <span
                key={letter}
                className={
                  challenge.focus_letters.includes(letter) ? "legend-chip focus" : "legend-chip"
                }
              >
                <code>{letter}</code> {name}
              </span>
            ))}
          </div>
          <p className="muted small">
            Loaded {pitchData.pitcher_count.toLocaleString()} pitchers from the {challenge.season} season.
          </p>

          <ExamplePitchers
            pitchers={examplePitchers}
            results={sampleResults}
            functionName={challenge.function_name}
          />
        </section>

        <section className="editor-col">
          <div className="editor-label">
            <code>{challenge.function_name}(pitches)</code>
          </div>
          <textarea
            className="code-editor"
            spellCheck={false}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <div className="actions">
            <button className="secondary" onClick={handleRun} disabled={running}>
              {running ? "Running…" : "Run"}
            </button>
            <button className="primary" onClick={handleSubmit} disabled={running}>
              Submit
            </button>
          </div>

          {actionError && <div className="error">{actionError}</div>}

          {runResult && !grade && (
            <div className={runResult.error ? "result error" : "result"}>
              {runResult.error ? (
                <p>{runResult.error}</p>
              ) : (
                <p>
                  Your function returned <code>true</code> for{" "}
                  <strong>{runResult.count.toLocaleString()}</strong> of{" "}
                  {runResult.total.toLocaleString()} pitchers.{" "}
                  <span className="muted">({runResult.durationMs} ms) — press Submit to grade.</span>
                </p>
              )}
            </div>
          )}

          {grade && (
            <div className={grade.correct ? "result correct" : "result wrong"}>
              <p className="result-headline">
                {grade.correct ? "✅ Correct!" : "❌ Not quite."}
              </p>
              <p>
                Your function matched <strong>{grade.claimed_count.toLocaleString()}</strong>{" "}
                pitchers. The real answer is{" "}
                <strong>{grade.expected_count.toLocaleString()}</strong>.
              </p>
            </div>
          )}
        </section>
      </div>

      <section className="history">
        <h3>Your submissions</h3>
        {submissions.length === 0 ? (
          <p className="muted">No submissions yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>You said</th>
                <th>Answer</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((s) => (
                <tr key={s.uuid}>
                  <td>{new Date(s.created_at_iso).toLocaleString()}</td>
                  <td>{s.claimed_count.toLocaleString()}</td>
                  <td>{s.expected_count.toLocaleString()}</td>
                  <td>{s.correct ? "✅" : "❌"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default ChallengePage;
